import React from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { createBrowserRouter, RouteObject } from 'react-router-dom'

import AppProvider from '@sputnik/AppProvider'
import BdcLanding from '@sputnik/pages/BdcLanding'
import CreditApplicationPage from '@sputnik/pages/CreditApplicationPages'
import BusinessAddressForm from '@sputnik/pages/CreditApplicationPages/BusinessAddressForm'
import ConnectBankAccountForm from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm'
import FinancialDetailsForm from '@sputnik/pages/CreditApplicationPages/FinancialDetailsForm'
import NameAndOwnershipForm from '@sputnik/pages/CreditApplicationPages/NameAndOwnershipForm'
import PeopleInfoForm from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm'
import ReviewForm from '@sputnik/pages/CreditApplicationPages/ReviewForm'
import Dashboard from '@sputnik/pages/DashboardPages/Dashboard'
import MobileDocumentUpload from '@sputnik/pages/MobileDocumentUpload/MobileDocumentUpload'
import NotFound from '@sputnik/pages/NotFound'
import AcceptingOfferPollingPage from '@sputnik/pages/PostApplicationPages/AcceptingOfferPollingPage'
import CreditLineOffersPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage'
import OfferAcceptedPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/OfferAcceptedPage'
import PrepayOfferPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/PrepayOfferPage/PrepayOfferPage'
import StandardCreditLineOfferPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/StandardCreditLineOfferPage'
import VariableCreditLineOfferPage from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/VariableCreditLineOfferPage'
import FrozenCredit from '@sputnik/pages/PostApplicationPages/FrozenCredit'
import MigrationSuccess from '@sputnik/pages/PostApplicationPages/MigrationSuccess'
import NoUWOfferPage from '@sputnik/pages/PostApplicationPages/NoUWOfferPage'
import SubmissionError from '@sputnik/pages/PostApplicationPages/SubmissionError'
import SubmittingApplicationPollingPage from '@sputnik/pages/PostApplicationPages/SubmittingApplicationPollingPage'
import WorkingOutDetails from '@sputnik/pages/PostApplicationPages/WorkingOutDetails'
import AutoLoginPage from '@sputnik/pages/PreApplicationPages/AutoLoginPage'
import GetToKnowYouPage from '@sputnik/pages/PreApplicationPages/GetToKnowYouPage'
import GoodFitSurveyPage from '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage'
import GoodFitResult from '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage/GoodFitResult'
import NotGoodFitResult from '@sputnik/pages/PreApplicationPages/GoodFitSurveyPage/NotGoodFitResult'
import PostLoginPage from '@sputnik/pages/PreApplicationPages/PostLoginPage/PostLoginPage'
import SignInPage from '@sputnik/pages/PreApplicationPages/SignInPage'
import CodeVerification from '@sputnik/pages/PreApplicationPages/SignInPage/CodeVerification'
import EmailVerification from '@sputnik/pages/PreApplicationPages/SignInPage/EmailVerification'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'

import RequireAuth from './RequireAuth'

const createSignInRoutes = (): RouteObject[] => {
  return [
    {
      path: PATHNAME_CONSTS.AUTH_PATH_ABSOLUTE,
      element: <SignInPage />,
      children: [
        {
          index: true,
          element: <EmailVerification />,
        },
        {
          path: PATHNAME_CONSTS.VERIFY_PATH,
          element: <CodeVerification />,
        },
      ],
    },
    {
      path: PATHNAME_CONSTS.MIGRATION_PATH_ABSOLUTE,
      element: <SignInPage />,
      children: [
        {
          index: true,
          element: <EmailVerification />,
        },
        {
          path: PATHNAME_CONSTS.VERIFY_PATH,
          element: <CodeVerification />,
        },
      ],
    },
    {
      path: PATHNAME_CONSTS.VERIFY_EMAIL_PATH,
      element: <AutoLoginPage />,
    },
    {
      path: PATHNAME_CONSTS.VERIFY_CODE_PATH,
      element: <AutoLoginPage />,
    },
    {
      path: PATHNAME_CONSTS.POST_LOGIN_PATH,
      element: <PostLoginPage />,
    },
  ]
}

export const createGoodFitSurveyRoutes = (): RouteObject[] => {
  return [
    {
      path: PATHNAME_CONSTS.GOOD_FIT_SURVEY_PATH,
      children: [
        {
          index: true,
          element: <GoodFitSurveyPage />,
        },
        {
          path: PATHNAME_CONSTS.GOOD_FIT_RESULT_PATH,
          element: (
            <GoogleReCaptchaProvider
              useEnterprise
              reCaptchaKey={window.__DivvyEnvironment.GOOGLE_RECAPTCHA_SITE_KEY ?? ''}
              scriptProps={{ appendTo: 'body', defer: true }}
            >
              <GoodFitResult />
            </GoogleReCaptchaProvider>
          ),
        },
        {
          path: PATHNAME_CONSTS.NOT_GOOD_FIT_RESULT_PATH,
          element: <NotGoodFitResult />,
        },
      ],
    },
  ]
}

export const createDashboardRoutes = (): RouteObject[] => {
  return [
    {
      path: '/',
      element: <Dashboard />,
    },
    {
      path: PATHNAME_CONSTS.DASHBOARD_PATH,
      element: <Dashboard />,
    },
  ]
}

const createCreditApplicationRoutes = (): RouteObject[] => {
  return [
    /* routes inside the credit application form */
    {
      path: 'app/:appId',
      element: <CreditApplicationPage />,
      children: [
        {
          path: PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH,
          element: <NameAndOwnershipForm />,
        },
        {
          path: PATHNAME_CONSTS.BUSINESS_ADDRESS_PATH,
          element: <BusinessAddressForm />,
        },
        {
          path: PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH,
          element: <FinancialDetailsForm />,
        },
        {
          path: PATHNAME_CONSTS.CONNECT_BANK_ACCOUNT_PATH,
          element: <ConnectBankAccountForm />,
        },
        {
          path: PATHNAME_CONSTS.PEOPLE_INFO_PATH,
          element: <PeopleInfoForm />,
        },
        {
          path: PATHNAME_CONSTS.REVIEW_PATH,
          element: <ReviewForm />,
        },
      ],
    },
    /* routes after submission: polling pages, offer pages, and offer result pages */
    {
      path: 'app/:appId',
      children: [
        {
          path: PATHNAME_CONSTS.SUBMITTING_APPLICATION,
          element: <SubmittingApplicationPollingPage />,
        },
        {
          path: PATHNAME_CONSTS.ACCEPTING_OFFER, // COM-9188: update designs for this component
          element: <AcceptingOfferPollingPage />,
        },
        {
          path: PATHNAME_CONSTS.SUBMISSION_ERROR_PATH,
          element: <SubmissionError />,
        },
        {
          path: PATHNAME_CONSTS.NO_OFFER_PATH,
          element: <NoUWOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.FROZEN_CREDIT_PATH,
          element: <FrozenCredit />,
        },
        {
          path: PATHNAME_CONSTS.WORKING_OUT_DETAILS_PATH,
          element: <WorkingOutDetails />,
        },
        {
          path: PATHNAME_CONSTS.MIGRATION_SUCCESS_PATH,
          element: <MigrationSuccess />,
        },
      ],
    },
    {
      path: `app/:appId/${PATHNAME_CONSTS.OFFERS_PATH}`,
      children: [
        { element: <CreditLineOffersPage />, index: true },
        {
          path: PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH,
          element: <StandardCreditLineOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH,
          element: <VariableCreditLineOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.PREPAY_OFFER_PATH,
          element: <PrepayOfferPage />,
        },
        {
          path: PATHNAME_CONSTS.OFFER_ACCEPTED_PATH,
          element: <OfferAcceptedPage />,
        },
      ],
    },
  ]
}

const createAuthenticatedRoutes = (): RouteObject[] => {
  return [
    {
      element: <RequireAuth />,
      children: [
        ...createDashboardRoutes(),
        ...createGoodFitSurveyRoutes(),
        ...createCreditApplicationRoutes(),
        {
          path: PATHNAME_CONSTS.MOBILE_DOCUMENT_UPLOAD_PATH,
          element: <MobileDocumentUpload />,
        },
        {
          path: PATHNAME_CONSTS.GET_TO_KNOW_YOU,
          element: <GetToKnowYouPage />,
        },
      ],
    },
  ]
}

export const createApplicationRoutes = () => {
  return createBrowserRouter([
    {
      element: <AppProvider />,
      children: [
        ...createSignInRoutes(),
        ...createAuthenticatedRoutes(),
        {
          path: PATHNAME_CONSTS.BDC_LANDING_PATH,
          element: <BdcLanding />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ])
}
